import { action, makeAutoObservable } from "mobx";
import { components } from "../../../../../api/spec";
import {
  sitchEventListAttendees,
  sitchEventCheckForNewUsersForRound,
  sitchEventRemoveAttendee,
  sitchEventSendReminder,
  sitchEventAddGuest,
} from "../../../../../api/sitch_events";
import EventStore from "../../event/store/eventStore";

export class GuestListStore {
  searchQuery = "";

  attendees: components["schemas"]["EventAttendeeSummarySchema"][] = [];

  activeFilter: components["schemas"]["EventAttendeeStatus"] | null = null;

  openDropdownGuestId: string | null = null;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  setSearchQuery(query: string) {
    this.searchQuery = query;
  }

  setActiveFilter(filter: components["schemas"]["EventAttendeeStatus"]) {
    if (this.activeFilter === filter) {
      this.activeFilter = null;
      return;
    }

    this.activeFilter = filter;
  }

  setOpenDropdownGuestId(guestId: string | null) {
    this.openDropdownGuestId = guestId;
  }

  toggleDropdown(guestId: string) {
    this.openDropdownGuestId =
      this.openDropdownGuestId === guestId ? null : guestId;
  }

  closeAllDropdowns() {
    this.openDropdownGuestId = null;
  }

  @action
  async loadAttendees(eventGuid: string, searchTerm?: string) {
    try {
      const result = await sitchEventListAttendees(eventGuid, searchTerm);
      this.attendees = result.attendees || [];
    } catch (err) {
      console.log(err);
    }
  }

  @action
  async removeGuest(guestId: string) {
    try {
      if (!EventStore.sitchEventData?.guid) {
        console.error("No event ID available");
        return;
      }
      await sitchEventRemoveAttendee(EventStore.sitchEventData.guid, guestId);
      await this.loadAttendees(EventStore.sitchEventData.guid);
    } catch (err) {
      console.log(err);
    }
  }

  @action
  async addGuest(payload: components["schemas"]["EventAddGuestRequest"]) {
    try {
      if (!EventStore.sitchEventData?.guid) {
        console.error("No event ID available");
        return;
      }
      await sitchEventAddGuest(payload);
      await this.loadAttendees(EventStore.sitchEventData.guid);
    } catch (err) {
      console.log(err);
    }
  }

  @action
  async checkForNewUsers() {
    if (!EventStore.sitchEventData?.current_round) return null;

    try {
      const result = await sitchEventCheckForNewUsersForRound(
        EventStore.sitchEventData.current_round,
      );

      if (result?.are_new_users_available && EventStore.sitchEventData?.guid) {
        await this.loadAttendees(EventStore.sitchEventData.guid);
        await EventStore.loadEventData(EventStore.sitchEventData.guid);
      }

      return result;
    } catch (err) {
      console.log(err);
      return null;
    }
  }

  @action
  async sendReminders() {
    if (!EventStore.sitchEventData?.guid) return null;

    const result = await sitchEventSendReminder({
      event: EventStore.sitchEventData.guid,
    });

    await this.loadAttendees(EventStore.sitchEventData.guid);

    return result;
  }

  get filterCounts() {
    return {
      not_signed_up: this.attendees.filter((a) => a.status === "not_signed_up")
        .length,
      started: this.attendees.filter(
        (a) => a.status === "started" || a.status === "pending_review",
      ).length,
      completed: this.attendees.filter((a) => a.status === "completed").length,
      not_started: this.attendees.filter((a) => a.status === "not_started")
        .length,
      pending_review: this.attendees.filter(
        (a) => a.status === "pending_review",
      ).length,
    };
  }

  get filteredGuests() {
    let filtered = this.attendees.filter((a) => !a.is_removed);

    if (this.searchQuery.trim()) {
      filtered = filtered.filter((attendee) =>
        (attendee.full_name || "")
          .toLowerCase()
          .includes(this.searchQuery.toLowerCase()),
      );
    }

    if (this.activeFilter !== null) {
      filtered = filtered.filter(
        (attendee) => attendee.status === this.activeFilter,
      );
    }

    return filtered;
  }

  reset() {
    this.searchQuery = "";
    this.activeFilter = null;
    this.openDropdownGuestId = null;
  }
}

export default new GuestListStore();
